import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Banknote, Building2, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Deal, BridgeFinanceType } from '../types/deal';

interface BridgemateDealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: Deal | null;
}

export function BridgemateDealModal({ open, onOpenChange, deal }: BridgemateDealModalProps) {
  const [formData, setFormData] = useState({
    clientName: deal?.clientName || '',
    clientEmail: '',
    clientPhone: '',
    propertyValue: deal?.finalAmount || deal?.offerAmount || 0,
    depositAvailable: '',
    bridgeAmount: '',
    currentPropertyValue: '',
    hasPropertyToSell: 'no',
    urgencyLevel: 'medium',
    additionalNotes: '',
    bridgeTypes: [] as BridgeFinanceType[],
    commissionPercentage: '',
    ratesOwed: '',
    taxesOwed: '',
    sellerFinanceAmount: '',
    sellerFinanceTerm: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleBridgeType = (type: BridgeFinanceType) => {
    const currentTypes = formData.bridgeTypes;
    if (currentTypes.includes(type)) {
      setFormData({ ...formData, bridgeTypes: currentTypes.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, bridgeTypes: [...currentTypes, type] });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.clientName || !formData.clientEmail || !formData.clientPhone || !formData.bridgeAmount || formData.bridgeTypes.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    // Simulate API call to Bridgemate
    setTimeout(() => {
      toast.success('Deal successfully sent to Bridgemate! They will contact your client within 24 hours.');
      setIsSubmitting(false);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        propertyValue: 0,
        depositAvailable: '',
        bridgeAmount: '',
        currentPropertyValue: '',
        hasPropertyToSell: 'no',
        urgencyLevel: 'medium',
        additionalNotes: '',
        bridgeTypes: [] as BridgeFinanceType[],
        commissionPercentage: '',
        ratesOwed: '',
        taxesOwed: '',
        sellerFinanceAmount: '',
        sellerFinanceTerm: '',
      });
    }, 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Banknote className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle>Send to Bridgemate</DialogTitle>
              <DialogDescription>
                Submit this deal for bridging finance approval
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {deal && (
          <div className="space-y-6">
            {/* Deal Summary */}
            <div className="p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">{deal.propertyTitle}</h4>
                <Badge variant="outline" className="bg-green-100 text-green-800">
                  Sold
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{deal.propertyAddress}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Deal Value:</span>
                <span className="font-semibold">R {(deal.finalAmount || deal.offerAmount).toLocaleString()}</span>
              </div>
            </div>

            {/* Client Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-primary" />
                <h4 className="font-semibold">Client Information</h4>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientName">
                    Client Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="clientName"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    placeholder="client@email.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientPhone">
                  Phone Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="clientPhone"
                  value={formData.clientPhone}
                  onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
            </div>

            {/* Finance Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Banknote className="w-4 h-4 text-primary" />
                <h4 className="font-semibold">Finance Details</h4>
              </div>

              {/* Bridge Type Selector */}
              <div className="space-y-2">
                <Label>
                  Bridge Finance Type <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="commission"
                      checked={formData.bridgeTypes.includes('commission')}
                      onCheckedChange={() => toggleBridgeType('commission')}
                    />
                    <div className="flex-1">
                      <Label htmlFor="commission" className="cursor-pointer font-medium">
                        Agent Commission Bridge
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Bridge finance for agent commission until property sale
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="rates-taxes"
                      checked={formData.bridgeTypes.includes('rates-taxes')}
                      onCheckedChange={() => toggleBridgeType('rates-taxes')}
                    />
                    <div className="flex-1">
                      <Label htmlFor="rates-taxes" className="cursor-pointer font-medium">
                        Rates & Taxes Bridge
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Bridge finance for outstanding rates and taxes
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="seller-finance"
                      checked={formData.bridgeTypes.includes('seller-finance')}
                      onCheckedChange={() => toggleBridgeType('seller-finance')}
                    />
                    <div className="flex-1">
                      <Label htmlFor="seller-finance" className="cursor-pointer font-medium">
                        Property Seller Finance
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Seller-backed financing for property purchase
                      </p>
                    </div>
                  </div>
                </div>
                {formData.bridgeTypes.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Select at least one bridge finance type
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="propertyValue">Purchase Property Value</Label>
                  <Input
                    id="propertyValue"
                    type="number"
                    value={formData.propertyValue}
                    onChange={(e) => setFormData({ ...formData, propertyValue: Number(e.target.value) })}
                    placeholder="R 0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="depositAvailable">
                    Deposit Available
                  </Label>
                  <Input
                    id="depositAvailable"
                    type="number"
                    value={formData.depositAvailable}
                    onChange={(e) => setFormData({ ...formData, depositAvailable: e.target.value })}
                    placeholder="R 0"
                  />
                </div>
              </div>

              {/* Agent Commission Bridge Fields */}
              {formData.bridgeTypes.includes('commission') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
                  <h5 className="font-semibold text-sm text-blue-900 dark:text-blue-100">Commission Bridge Details</h5>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="commissionPercentage" className="text-xs">
                        Commission % <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="commissionPercentage"
                        type="number"
                        step="0.1"
                        value={formData.commissionPercentage}
                        onChange={(e) => setFormData({ ...formData, commissionPercentage: e.target.value })}
                        placeholder="e.g., 5.5"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bridgeAmount" className="text-xs">
                        Commission Amount <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="bridgeAmount"
                        type="number"
                        value={formData.bridgeAmount}
                        onChange={(e) => setFormData({ ...formData, bridgeAmount: e.target.value })}
                        placeholder="R 0"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Agent commission will be bridged until seller's property is sold
                  </p>
                </div>
              )}

              {/* Rates & Taxes Bridge Fields */}
              {formData.bridgeTypes.includes('rates-taxes') && (
                <div className="p-4 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg space-y-3">
                  <h5 className="font-semibold text-sm text-orange-900 dark:text-orange-100">Rates & Taxes Bridge Details</h5>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="ratesOwed" className="text-xs">
                        Outstanding Rates <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="ratesOwed"
                        type="number"
                        value={formData.ratesOwed}
                        onChange={(e) => setFormData({ ...formData, ratesOwed: e.target.value })}
                        placeholder="R 0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="taxesOwed" className="text-xs">
                        Outstanding Taxes <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="taxesOwed"
                        type="number"
                        value={formData.taxesOwed}
                        onChange={(e) => setFormData({ ...formData, taxesOwed: e.target.value })}
                        placeholder="R 0"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bridgeAmount" className="text-xs">
                      Total Bridge Amount <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="bridgeAmount"
                      type="number"
                      value={formData.bridgeAmount}
                      onChange={(e) => setFormData({ ...formData, bridgeAmount: e.target.value })}
                      placeholder="R 0"
                    />
                  </div>

                  <p className="text-xs text-orange-700 dark:text-orange-300">
                    Bridge finance to clear outstanding rates and taxes for property transfer
                  </p>
                </div>
              )}

              {/* Seller Finance Fields */}
              {formData.bridgeTypes.includes('seller-finance') && (
                <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-3">
                  <h5 className="font-semibold text-sm text-purple-900 dark:text-purple-100">Seller Finance Details</h5>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="sellerFinanceAmount" className="text-xs">
                        Seller Finance Amount <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="sellerFinanceAmount"
                        type="number"
                        value={formData.sellerFinanceAmount}
                        onChange={(e) => setFormData({ ...formData, sellerFinanceAmount: e.target.value })}
                        placeholder="R 0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sellerFinanceTerm" className="text-xs">
                        Repayment Term (months) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="sellerFinanceTerm"
                        type="number"
                        value={formData.sellerFinanceTerm}
                        onChange={(e) => setFormData({ ...formData, sellerFinanceTerm: e.target.value })}
                        placeholder="e.g., 12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bridgeAmount" className="text-xs">
                      Bridge Amount Required <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="bridgeAmount"
                      type="number"
                      value={formData.bridgeAmount}
                      onChange={(e) => setFormData({ ...formData, bridgeAmount: e.target.value })}
                      placeholder="R 0"
                    />
                  </div>

                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    Seller-backed financing with structured repayment plan
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="hasPropertyToSell">Does client have property to sell?</Label>
                <Select
                  value={formData.hasPropertyToSell}
                  onValueChange={(value) => setFormData({ ...formData, hasPropertyToSell: value })}
                >
                  <SelectTrigger id="hasPropertyToSell">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes - Listed</SelectItem>
                    <SelectItem value="yes-offer">Yes - Offer Accepted</SelectItem>
                    <SelectItem value="yes-sold">Yes - Already Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.hasPropertyToSell !== 'no' && (
                <div className="space-y-2">
                  <Label htmlFor="currentPropertyValue">Current Property Value</Label>
                  <Input
                    id="currentPropertyValue"
                    type="number"
                    value={formData.currentPropertyValue}
                    onChange={(e) => setFormData({ ...formData, currentPropertyValue: e.target.value })}
                    placeholder="R 0"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="urgencyLevel">Urgency Level</Label>
                <Select
                  value={formData.urgencyLevel}
                  onValueChange={(value) => setFormData({ ...formData, urgencyLevel: value })}
                >
                  <SelectTrigger id="urgencyLevel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - 30+ days</SelectItem>
                    <SelectItem value="medium">Medium - 14-30 days</SelectItem>
                    <SelectItem value="high">High - 7-14 days</SelectItem>
                    <SelectItem value="urgent">Urgent - Within 7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Additional Notes</Label>
              <Textarea
                id="additionalNotes"
                value={formData.additionalNotes}
                onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                placeholder="Any additional information that might help with the bridging finance application..."
                rows={3}
              />
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    What happens next?
                  </p>
                  <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• Bridgemate will review the application within 24 hours</li>
                    <li>• They'll contact your client directly via email/phone</li>
                    <li>• You'll receive a copy of all correspondence</li>
                    <li>• Approval typically takes 2-5 business days</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                data-testid="bridgemate-deal-cancel-button"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="bridgemate-deal-submit-button"
              >
                {isSubmitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to Bridgemate
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}